// Updated candidate-analyze-page.ts
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  EnvironmentInjector,
  inject,
  resource,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Badge } from '@shared/components/badge/badge';
import { Button } from '@shared/components/button/button';
import { CodeEditor } from '@shared/components/code-editor/code-editor';
import { ViewTestCase } from '@shared/components/view-test-case/view-test-case';
import {
  AnalyzeData,
  AnalyzeProblem,
  AnalyzeTestCaseResult,
} from '@shared/models/analyze';

@Component({
  selector: 'app-candidates-page',
  imports: [Badge, Button, CodeEditor, ViewTestCase],
  templateUrl: './candidate-analyze-page.html',
})
export class CandidateAnalyzePage implements AfterViewInit {
  // Dependencies
  private readonly apiClient = inject(ApiClient);
  private readonly ActivatedRoute = inject(ActivatedRoute);
  private readonly injector = inject(EnvironmentInjector);

  // Signals
  readonly selectedProblem = signal<AnalyzeProblem | null>(null);
  readonly candidateCode = signal<string>('');
  readonly codeLanguage = signal<string>('');
  readonly testCaseResult = signal<AnalyzeTestCaseResult[]>([]);

  readonly selectedTestCaseResult = signal<AnalyzeTestCaseResult | null>(null);
  createAtSubmission = signal<Date | null>(null);

  private candidateId = '';

  readonly analyze = resource({
    loader: () => this.apiClient.getAnalyzeById(this.candidateId),
  });

  readonly isAnalyzeLoading = computed(
    () => this.analyze.status() === 'loading'
  );

  readonly analyzeData = computed(() => {
    return this.analyze.value() as AnalyzeData | undefined;
  });

  readonly submission = computed(() => {
    const data = this.analyzeData();
    if (!data?.submission?.length) return null;
    return data.submission[0];
  });

  readonly problems = computed(() => {
    const data = this.analyzeData();

    return data?.problems || [];
  });

  constructor() {
    this.ActivatedRoute.paramMap.subscribe(params => {
      const id = params.get('candidateId') || '';
      this.candidateId = id;
    });
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const submission = this.submission();
        if (submission) {
          this.createAtSubmission.set(submission.completedAt ?? null);
        }
      });
    });
    this.setupComponentEffects();
  }

  // เปิด Clarity Recordings section
  openClarityRecordings(): void {
    const email = this.analyzeData()?.email || '';
    const completedAt = this.createAtSubmission();
    const msPerDay = 24 * 60 * 60 * 1000;
    let dateParam = 'Today';

    if (completedAt) {
      // robust parse
      const d0 = new Date(completedAt);
      const today0 = new Date();

      // นอร์มัลไลซ์เป็นเที่ยงคืน “ตามเขตเวลาเครื่อง”
      const dDay = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate());
      const tDay = new Date(
        today0.getFullYear(),
        today0.getMonth(),
        today0.getDate()
      );

      const diffDays = Math.round((tDay.getTime() - dDay.getTime()) / msPerDay);

      if (diffDays === 0) {
        dateParam = 'Today';
      } else if (diffDays === 1) {
        dateParam = 'Yesterday';
      } else if (diffDays >= 2 && diffDays <= 7) {
        dateParam = `Last${diffDays}days`; // ตัวอย่าง: Last3days
      } else {
        dateParam = 'Last30days';
      }
    }
    if (email) {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard
          .writeText(email)
          .then(() => console.log(`📋 Copied email to clipboard: ${email}`))
          .catch(err => console.warn('Clipboard copy failed:', err));
      } else {
        console.warn('Clipboard API not supported');
      }
    }
    const clarityUrl = `https://clarity.microsoft.com/projects/view/spv50f8e7o/impressions${
      email
        ? `?Variables=email%3A${encodeURIComponent(email)}&date=${dateParam}`
        : ''
    }`;
    window.open(clarityUrl, '_blank', 'noopener,noreferrer');
  }
  // เปิด Clarity Dashboard หลัก
  openClarityDashboard(): void {
    const email = this.analyzeData()?.email || '';
    const completedAt = this.createAtSubmission();

    const msPerDay = 24 * 60 * 60 * 1000;
    let dateParam = 'Today';

    if (completedAt) {
      // robust parse
      const d0 = new Date(completedAt);
      const today0 = new Date();

      // นอร์มัลไลซ์เป็นเที่ยงคืน “ตามเขตเวลาเครื่อง”
      const dDay = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate());
      const tDay = new Date(
        today0.getFullYear(),
        today0.getMonth(),
        today0.getDate()
      );

      const diffDays = Math.round((tDay.getTime() - dDay.getTime()) / msPerDay);

      if (diffDays === 0) {
        dateParam = 'Today';
      } else if (diffDays === 1) {
        dateParam = 'Yesterday';
      } else if (diffDays >= 2 && diffDays <= 7) {
        dateParam = `Last${diffDays}days`; // ตัวอย่าง: Last3days
      } else {
        dateParam = 'Last30days';
      }
    }
    if (email) {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard
          .writeText(email)
          .then(() => console.log(`📋 Copied email to clipboard: ${email}`))
          .catch(err => console.warn('Clipboard copy failed:', err));
      } else {
        console.warn('Clipboard API not supported');
      }
    }
    const clarityUrl = `https://clarity.microsoft.com/projects/view/spv50f8e7o/dashboard${
      email
        ? `?Variables=email%3A${encodeURIComponent(email)}&date=${dateParam}`
        : ''
    }`;
    window.open(clarityUrl, '_blank', 'noopener,noreferrer');
  }

  selectProblem(problem: AnalyzeProblem): void {
    console.log('Selecting problem:', problem.testCaseResults);

    this.selectedProblem.set(problem);
    this.candidateCode.set(problem.bestCode || '// No code available');
    this.codeLanguage.set(this.mapLanguageToEditorLanguage(problem.language));
    this.testCaseResult.set(problem.testCaseResults || []);

    this.selectedTestCaseResult.set(
      problem.testCaseResults && problem.testCaseResults.length > 0
        ? problem.testCaseResults[0]
        : null
    );

    // Scroll to top of the page only on mobile devices
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Helper method to map language names to code editor language identifiers
  private mapLanguageToEditorLanguage(language: string): string {
    return !language ? 'javascript' : language.toLowerCase();
  }

  // Helper method to format time
  formatTime(timeUse: number, duration: number): string {
    const minutesUsed = Math.round(timeUse / 60);
    const minutesAllocated = Math.round(duration);
    return `${minutesUsed} minutes of ${minutesAllocated} minutes allocated.`;
  }

  private setupComponentEffects(): void {
    runInInjectionContext(this.injector, () => {
      effect(() => {
        const problems = this.problems();
        if (!this.selectedProblem() && problems.length > 0) {
          this.selectProblem(problems[0]);
        }
      });
    });
  }
}
